import apiClient from './config';

const EARLY_ACCESS_PUBLIC_BASE = '/api/auth/early-access';
const EARLY_ACCESS_ADMIN_BASE = '/api/superadmin/early-access';

export interface EarlyAccessApplyPayload {
    full_name: string;
    email: string;
    company_name: string;
    notes?: string;
}

export interface EarlyAccessApplyResponse {
    message: string;
    id: number;
}

export interface EarlyAccessSettings {
    company_email_only: boolean;
}

export interface EarlyAccessApplication {
    id: number;
    email: string;
    full_name: string;
    company_name: string;
    notes: string | null;
    status: 'pending' | 'approved' | 'rejected';
    created_at: string | null;
    approved_at: string | null;
    approved_by_user_id: number | null;
    token_expires_at: string | null;
    signup_completed: boolean;
    signup_completed_at: string | null;
    beta_quota_enabled?: boolean;
    beta_free_token_quota: number | null;
    beta_free_quota_expiry_days: number | null;
    beta_quota_applied_org_id: number | null;
    beta_quota_applied_at: string | null;
    beta_quota_locked?: boolean;
    company_email_only_at_submission?: boolean | null;
}

export interface EarlyAccessListResponse {
    items: EarlyAccessApplication[];
    total: number;
    page: number;
    page_size: number;
}

export type EarlyAccessStatusFilter = 'all' | 'pending' | 'approved' | 'rejected';

export interface EarlyAccessQuotaPayload {
    beta_free_token_quota: number;
    beta_free_quota_expiry_days: number;
}

export async function applyForEarlyAccess(
    payload: EarlyAccessApplyPayload,
): Promise<EarlyAccessApplyResponse> {
    const { data } = await apiClient.post<EarlyAccessApplyResponse>(
        `${EARLY_ACCESS_PUBLIC_BASE}/apply`,
        payload,
    );
    return data;
}

export async function listEarlyAccessApplications(params?: {
    status?: EarlyAccessStatusFilter;
    page?: number;
    page_size?: number;
}): Promise<EarlyAccessListResponse> {
    const { data } = await apiClient.get<EarlyAccessListResponse>(
        `${EARLY_ACCESS_ADMIN_BASE}/applications`,
        { params },
    );
    return data;
}

export async function approveEarlyAccessApplication(
    id: number,
    payload?: EarlyAccessQuotaPayload,
): Promise<EarlyAccessApplication> {
    const { data } = await apiClient.post<EarlyAccessApplication>(
        `${EARLY_ACCESS_ADMIN_BASE}/applications/${id}/approve`,
        payload ?? undefined,
    );
    return data;
}

export async function getEarlyAccessSettings(): Promise<EarlyAccessSettings> {
    const { data } = await apiClient.get<EarlyAccessSettings>(
        `${EARLY_ACCESS_PUBLIC_BASE}/settings`,
    );
    return data;
}

export async function updateEarlyAccessSettings(
    payload: EarlyAccessSettings,
): Promise<EarlyAccessSettings> {
    const { data } = await apiClient.put<EarlyAccessSettings>(
        `${EARLY_ACCESS_ADMIN_BASE}/settings`,
        payload,
    );
    return data;
}

export async function updateEarlyAccessQuota(
    id: number,
    payload: EarlyAccessQuotaPayload,
): Promise<EarlyAccessApplication> {
    const { data } = await apiClient.patch<EarlyAccessApplication>(
        `${EARLY_ACCESS_ADMIN_BASE}/applications/${id}/quota`,
        payload,
    );
    return data;
}
